<template>
	<div v-if="props.cartProducts">
		<div
			v-for="(products, index) in state.filteredProducts"
			:key="index"
			class="order_content"
		>
			<div class="title_bar">
				<h2>
					{{ products.length }} {{ $t("general.products", { count: products.length }) }}
					{{ $t("general.shipped_from") }} [{{ products[0].warehouse_name }}] ([{{
						getWarehouseCountry(products[0].warehouse_country_alpha2)
					}}]):
				</h2>
			</div>
			<Table
				:key="JSON.stringify(products)"
				:source="products"
				:selectable="false"
			>
				<TableColumn
					:label="$t('components.cart.col_product')"
					prop=""
					:sortable="true"
					:mobile-config="{ isHeader: true }"
					:width="45"
				>
					<template #default="scoped">
						<div class="product_column">
							<NuxtLink
								:to="`/textiles/${scoped.row?.textile_id}?item=${scoped.row?.textile_item_id}`"
								class="block w-[90px] h-[60px] rounded bg-black border-neutral-700 mr-[12px] shrink-0"
							>
								<img
									:src="`${imgixEcmOdcUrl}/organizations/${scoped.row?.organization_id}/products/${scoped.row?.textile_id}/${scoped.row?.textile_item_id}/images/${scoped.row?.image_id}`"
								/>
							</NuxtLink>
							<div class="product_info">
								<NuxtLink
									:to="`/textiles/${scoped.row?.textile_id}?item=${scoped.row?.textile_item_id}`"
									class="block flex flex-col"
								>
									<span class="name">{{
										organizationTextileCodeName(
											scoped.row.organization_textile_code,
											scoped.row.organization_textile_name
										)
									}}</span>
									<span class="color">{{ scoped.row.organization_textile_item_code }}</span>
								</NuxtLink>
								<span class="ships_within">
									<Icon
										name="md-local_shipping"
										class="mr-[8px]"
									/>
									{{ $t("components.cart.shipping_duration") }}
								</span>
								<span
									class="remove_from_cart"
									@click="removeFromCart(scoped.row)"
								>
									{{ $t("components.cart.remove") }}
								</span>
							</div>
						</div>
					</template>
				</TableColumn>
				<TableColumn
					:label="$t('components.cart.col_qty')"
					prop=""
					:sortable="true"
					:mobile-config="{ showLabel: true }"
					:width="20"
				>
					<template #default="scoped">
						<div class="flex items-center">
							<v-responsive
								max-width="90px"
								min-width="75px"
							>
								<TextInput
									:model-value="scoped.row.meter_quantity"
									density="compact"
									variant="outlined"
									@change="(e: any) => changeQuantity(scoped.row, e.target.value * 1)"
								/>
							</v-responsive>
							<span class="pl-[4px]">{{ $t("components.cart.meters") }}</span>
						</div>
					</template>
				</TableColumn>
				<TableColumn
					:label="$t('components.cart.col_price_per_meter')"
					prop=""
					:sortable="true"
					:mobile-config="{ showLabel: true }"
					:width="18"
				>
					<template #default="scoped">
						<span>
							${{ scoped.row.unit_price }}
							<span>USD</span>
						</span>
					</template>
				</TableColumn>
				<TableColumn
					:label="$t('components.cart.col_subtotal')"
					prop=""
					:sortable="true"
					:mobile-config="{ showLabel: true }"
					:width="14"
				>
					<template #default="scoped">
						<span>
							${{ (scoped.row.meter_quantity * scoped.row.unit_price).toFixed(2) }}
							<span>USD</span>
						</span>
					</template>
				</TableColumn>
				<template #rowMobile>
					<div class="row_actions">
						<span class="ships_within">
							<Icon
								name="md-local_shipping"
								class="mr-[8px]"
							/>
							{{ $t("components.cart.shipping_duration") }}
						</span>
						<span class="remove_from_cart">{{ $t("components.cart.remove") }}</span>
					</div>
				</template>
			</Table>
			<div v-if="!isCheckout">
				<div
					v-if="!showNotes"
					class="add_notes"
				>
					<h3
						class="tg-link-color"
						@click="toggleNotes"
					>
						{{ $t("components.cart.add_note") }}
					</h3>
				</div>
				<div
					v-else
					class="notes"
				>
					<h3>{{ $t("components.cart.note") }}<span class="text-red-200">*</span></h3>
					<v-textarea
						variant="underlined"
						rows="2"
						row-height="20"
						class="mb-[16px]"
					/>
					<span
						class="text-red-200 cursor-pointer"
						@click="toggleNotes"
					>
						{{ $t("components.cart.delete") }}
					</span>
				</div>
			</div>
		</div>
	</div>
	<div
		v-else
		class="order_content"
	>
		<div class="title_bar">
			<h2 v-if="props.numOfSamples">
				{{ props.numOfSamples }} {{ $t("general.samples", { count: numOfSamples }) }} shipped by Tengiva:
			</h2>
		</div>
		<Table
			:key="JSON.stringify(state.filteredSamples)"
			:source="state.filteredSamples"
			:selectable="false"
		>
			<TableColumn
				label="PRODUCT"
				prop="name"
				:sortable="true"
				:mobile-config="{ isHeader: true }"
				:width="55"
			>
				<template #default="scoped">
					<div class="product_column">
						<NuxtLink
							:to="`/textiles/${scoped.row?.textile_id}?item=${scoped.row?.textile_item_id}`"
							class="block w-[90px] h-[60px] rounded bg-black border-neutral-700 mr-[12px] shrink-0"
						>
							<!-- Using textile_organization_id to get thumbnail, as the organization_id for samples is Tengiva's,
                             but we need the organization_id of the textile's merchant to get the correct image url -->
							<img
								:src="`${imgixEcmOdcUrl}/organizations/${scoped.row?.textile_organization_id}/products/${scoped.row?.textile_id}/${scoped.row?.textile_item_id}/images/${scoped.row?.image_id}`"
							/>
						</NuxtLink>
						<div class="product_info">
							<NuxtLink
								:to="`/textiles/${scoped.row?.textile_id}?item=${scoped.row?.textile_item_id}`"
								class="block flex flex-col"
							>
								<span class="name">{{
									organizationTextileCodeName(
										scoped.row.organization_textile_code,
										scoped.row.organization_textile_name
									)
								}}</span>
								<span class="color">{{ scoped.row.organization_textile_item_code }}</span>
							</NuxtLink>
							<span
								class="remove_from_cart"
								@click="removeFromCart(scoped.row)"
							>
								{{ $t("components.cart.remove") }}
							</span>
						</div>
					</div>
				</template>
			</TableColumn>
			<TableColumn
				label="QTY"
				prop=""
				:sortable="true"
				:mobile-config="{ showLabel: true }"
				:width="25"
			>
				<template #default>
					<span>1 {{ $t("general.samples") }} (14x25.5 cm)</span>
				</template>
			</TableColumn>
			<TableColumn
				label="SUBTOTAL"
				prop=""
				:sortable="true"
				:mobile-config="{ showLabel: true }"
				:width="20"
			>
				<template #default>
					<span>
						$ 3.50
						<span>USD</span>
					</span>
				</template>
			</TableColumn>
		</Table>
	</div>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useGtag } from "vue-gtag-next";
import Table from "@/components/TableV1/Table.vue";
import TableColumn from "@/components/TableV1/TableColumn.vue";
import { mapCartItemToGAItem } from "@/plugins/gtag.client";
import type { Toast } from "@tengiva/components";

const { t } = useI18n();

interface OrderProps {
	isCheckout?: boolean;
	cartProducts?: any;
	cartSamples?: Array<{ [key: string]: any }>;
	numOfProducts?: number;
	numOfSamples?: number;
}
const props = defineProps<OrderProps>();
const { imgixEcmOdcUrl } = storeToRefs(usePlatformStore());

interface PageState {
	filteredProducts: any[];
	filteredSamples: any[];
}
const state = reactive<PageState>({
	filteredProducts: [],
	filteredSamples: [],
});

const showNotes = ref(false);
const toggleNotes = () => {
	showNotes.value = !showNotes.value;
};

function updateState() {
	state.filteredProducts = [];
	if (props.cartProducts) {
		for (let i = 0; i < props.cartProducts.length; i++) {
			state.filteredProducts[i] = props.cartProducts[i][0]?.map((item: any) => ({ ...item }));
		}
	}
	if (props.cartSamples) {
		state.filteredSamples = props.cartSamples.map(item => ({ ...item }));
	}
}

const { $countries, $toast, $sentry } = useNuxtApp();

function getWarehouseCountry(alpha2: string) {
	return $countries.getAlpha2ToName(alpha2);
}

onMounted(() => {
	updateState();
});

const gtag = useGtag();
const { editTextileItemQuantity, removeTextileItemFromCart } = useTextileStore();
const { getCartData } = useCartStore();

async function changeQuantity(textileItem: any, num: any) {
	if (isNaN(num)) {
		updateState();
	} else {
		const item = mapCartItemToGAItem(textileItem);
		gtag.event("remove_from_cart", {
			currency: "USD",
			value: (item.price || 0) * (item.quantity || 0),
			items: [item],
		});

		let result: string;

		// Minimum qty for one textile item is 5 meters
		if (num < 5) {
			($toast as Toast)({
				message: t("pages.cart.minimum_5"),
				type: "warning",
				location: "top right",
				closable: true,
				duration: 10000,
			});
			// send request to set number 5
			result = await editTextileItemQuantity(textileItem.id, 5);
		} else {
			if (num % 5) {
				($toast as Toast)({
					message: t("pages.single_product.order_sample.quantity_error.multiple_5"),
					type: "warning",
					location: "top right",
					closable: true,
					duration: 10000,
				});
			}
			num = Math.ceil(Math.ceil(num) / 5) * 5;
			// send request to set num
			result = await editTextileItemQuantity(textileItem.id, num);
			// TODO: update textile item price, cause it depends on volume
		}

		if (result === "Attempted quantity is superior to stocks.") {
			($toast as Toast)({
				message: t("pages.cart.exceed_stock"),
				type: "warning",
				location: "top right",
				closable: true,
				duration: 10000,
			});
		} else if (result !== "Success") {
			$sentry.captureException(`Error while edit quantity in cart, textileItemId: ${textileItem.id}`, {
				originalException: result,
			});
		}

		// re-fetch cart data to display
		await getCartData();
		updateState();

		item.quantity = num;
		gtag.event("add_to_cart", {
			currency: "USD",
			value: (item.price || 0) * (item.quantity || 0),
			items: [item],
		});
	}
}

const removeFromCart = async (textileItem: any) => {
	const item = mapCartItemToGAItem(textileItem);
	await removeTextileItemFromCart(textileItem.id);

	// re-fetch cart data to display
	await getCartData();
	updateState();

	gtag.event("remove_from_cart", {
		currency: "USD",
		value: (item.price || 0) * (item.quantity || 0),
		items: [item],
	});
};
</script>
<style lang="scss" scoped>
.order_content {
	@apply w-full rounded-[4px] overflow-visible;
	@screen mobile {
		@apply border-x-0 w-full shadow-none px-0 rounded-none;
	}

	.title_bar {
		@apply flex justify-between py-[16px] items-center;
		@screen mobile {
			@apply px-[15px] border-b-[3px] border-neutral-100;
		}

		h2 {
			@apply text-[16px] leading-[24px] font-medium text-black;
		}
	}

	.product_column {
		@apply flex;

		.product_info {
			@apply flex flex-col;

			.name {
				@apply text-[14px] leading-[20px] text-black;
			}

			.color {
				@apply text-[16px] leading-[24px] font-medium text-black mb-[16px];
			}

			.ships_within,
			.remove_from_cart {
				@apply text-[14px] leading-[20px] text-neutral-700;
			}

			.ships_within {
				@apply flex items-center mb-[12px] mobile:hidden;
			}

			.remove_from_cart {
				@apply underline cursor-pointer mobile:hidden;
			}
		}
	}

	.row_actions {
		@apply flex justify-between items-center;

		.ships_within {
			@apply flex items-center;
		}

		.remove_from_cart {
			@apply underline cursor-pointer;
		}
	}

	.add_notes {
		@apply p-[16px];
		h3 {
			@apply text-[14px] leading-[20px]  cursor-pointer;
		}
	}

	.notes {
		@apply p-[16px];
		h3 {
			@apply text-[14px] leading-[20px] text-neutral-700;
		}
	}

	:deep(.table-wrapper) {
		@apply mobile:w-full mobile:mx-0;
	}

	:deep(.row .row-grid) {
		@apply h-[150px] pt-[50px] border-b;
	}

	:deep(.table-wrapper .table .body-mobile .row) {
		@apply px-[15px] border-b;
	}

	.v-input {
		:deep(.v-input__details) {
			@apply hidden;
		}
	}
}
</style>
